import type { ModelInfo } from '@paradigm/terminal-core';

/**
 * Parse and group models from the new `provider/company/model/version` id
 * convention returned by the gateway's `/api/models`.
 *
 * Examples:
 *   `bedrock/anthropic/opus/4.8`   → provider=bedrock, company=anthropic, model=opus, version=4.8
 *   `qwen/qwen/qwen-3.7/max`      → provider=qwen,    company=qwen,     model=qwen-3.7, version=max
 *   `bedrock/deepseek/deepseek-v3/`→ provider=bedrock,  company=deepseek, model=deepseek-v3, version=""
 *
 * Grouping:
 *  - Primary grouping is by **company** (second segment), not provider.
 *  - Anthropic always sorts first; "Other" always sorts last.
 *  - Within a company group, models with multiple versions get a sub-group
 *    with an expandable caret in the picker.
 *  - Models available from multiple providers (e.g. kimi-k3 on both
 *    bedrock and fireworks-ai) get a provider tag to differentiate.
 */

/** Parsed segments of a `provider/company/model/version` id. */
export interface ParsedModelId {
  readonly provider: string;
  readonly company: string;
  readonly model: string;
  readonly version: string;
}

/**
 * Parse `provider/company/model/version` — tolerates a trailing slash
 * (empty version) and fewer segments (best-effort).
 *
 * The `DEFAULT` version sentinel (appended to versionless models to avoid
 * trailing-slash issues with openclaw) is stripped for display — the raw
 * model `id` (with `DEFAULT`) is preserved for the `/model` command.
 */
export function parseModelId(id: string): ParsedModelId {
  // Strip the `dtmodel/` openclaw provider prefix — it's an internal routing
  // detail, not part of the provider/company/model/version structure.
  let cleaned = id.replace(/^dtmodel\//u, '');
  // Strip trailing slash.
  if (cleaned.endsWith('/')) cleaned = cleaned.slice(0, -1);
  const parts = cleaned.split('/');
  const rawVersion = parts[3] ?? '';
  return {
    provider: parts[0] ?? '',
    company: parts[1] ?? '',
    model: parts[2] ?? '',
    version: /^default$/iu.test(rawVersion) ? '' : rawVersion,
  };
}

const COMPANY_LABELS: ReadonlyMap<string, string> = new Map([
  ['anthropic', 'Anthropic'],
  ['amazon', 'Amazon'],
  ['deepseek', 'DeepSeek'],
  ['google', 'Google'],
  ['meta', 'Meta'],
  ['mistral', 'Mistral'],
  ['openai', 'OpenAI'],
  ['qwen', 'Qwen'],
  ['z-ai', 'Z.AI'],
  ['moonshot-ai', 'Moonshot AI'],
]);

const OTHER_LABEL = 'Other';

export function getCompanyLabel(id: string): string {
  const { company } = parseModelId(id);
  if (company.length === 0) return OTHER_LABEL;
  const known = COMPANY_LABELS.get(company.toLowerCase());
  if (known != null) return known;
  return company.charAt(0).toUpperCase() + company.slice(1);
}

// ── Provider-group (old API, still used by getProviderLabel tests) ──────

/** @deprecated Use getCompanyLabel — kept only for backward compat. */
export function getProviderLabel(id: string): string {
  return getCompanyLabel(id);
}

export interface ModelProviderGroup {
  readonly label: string;
  readonly models: readonly ModelInfo[];
}

export function groupModelsByProvider(
  models: readonly ModelInfo[],
): readonly ModelProviderGroup[] {
  return groupModelsByCompany(models);
}

// ── Company-group (new primary API) ────────────────────────────────────

export function groupModelsByCompany(
  models: readonly ModelInfo[],
): readonly ModelProviderGroup[] {
  const byLabel = new Map<string, ModelInfo[]>();
  for (const m of models) {
    const label = getCompanyLabel(m.id);
    const bucket = byLabel.get(label);
    if (bucket == null) byLabel.set(label, [m]);
    else bucket.push(m);
  }
  const labels = [...byLabel.keys()].sort((a, b) => {
    if (a === b) return 0;
    if (a === 'Anthropic') return -1;
    if (b === 'Anthropic') return 1;
    if (a === OTHER_LABEL) return 1;
    if (b === OTHER_LABEL) return -1;
    // Alphabetical, independent of catalog order.
    return a.localeCompare(b);
  });
  return labels.map((label) => ({
    label,
    models: byLabel.get(label) ?? [],
  }));
}

// ── Ordering ────────────────────────────────────────────────────────────
//
// How the picker decides what goes where, and in what order. When a new
// model from the gateway lands in the wrong place, debug against this:
//
// GROUPING (which row a model belongs to) is purely the model slug
// (segment 3 of `provider/company/model/version`). Same slug → same row;
// multiple version segments under one slug → flyout. The slug boundary is
// intentional: `kimi-k2.5` vs `kimi-k3` are separate rows, while `opus/5`
// and `opus/4.8` share one. Ordering below NEVER regroups — it only sorts.
//
// ROW ORDER within a company group (groupByModelAndVersion):
//   1. Rows whose every entry is `legacy: true` sink to the bottom.
//   2. FAMILY_RANK index, when the company has a table (Fable > Opus >
//      Sonnet > Haiku). Unlisted bases rank after listed ones.
//   3. Base name, alphabetically.
//   4. Trailing slug version, DESCENDING — SLUG_VERSION_RE splits the slug
//      into base + version tuple:
//        glm-5.2         → base `glm`,     tuple [5,2]
//        deepseek-v3.2   → base `deepseek`, tuple [3,2]  (letter PREFIX ok)
//        kimi-k2.5       → base `kimi`,    tuple [2,5]
//        mistral-large-3 → base `mistral-large`, tuple [3]
//        gpt-oss / opus  → whole slug, tuple []          (no match)
//      so GLM renders 5.2, 5, 4.7 and Kimi renders K3, K2.5.
//
// FLYOUT ORDER within a row (sortVersionEntries), where the version segment
// mixes versions and variants (`5`, `4.8`, `max`, `120b`, `coder-480b`):
//   1. `legacy: true` entries sink to the bottom.
//   2. Leading version tuple, descending (`5.1` > `5-max` > `5`).
//   3. Tier suffix: `max` > bare > `mini`/`lite`/`flash`/`nano`.
//   4. Param count, descending (`coder-480b` > `235b`).
//   5. Raw segment, then provider (multi-provider tiebreak).
//
// Version tuples compare element-wise as INTEGERS, absent = lowest, so
// 5.2 > 5.1 > 5 > 4.10 > 4.9 (parseFloat would put 4.9 above 4.10).
//
// A new model sorting wrong usually means one of:
//   - a new family with no derivable rank → add it to FAMILY_RANK;
//   - a slug whose trailing segment isn't `-[letter]digits[.digits]`
//     (e.g. `-3b`, `-beta`) → parsed as versionless, sorts by base name
//     only; extend SLUG_VERSION_RE if it should count as a version;
//   - a new variant token (`turbo`, `preview`, …) → unknown tokens are
//     ignored (tier 0); add to parseVersionSegment's tier lists if it
//     should rank above/below the bare model;
//   - an acronym rendering as title case (`Glm`) → add to WORD_CASE.

/**
 * Capability order for model families that can't be derived from the id —
 * nothing in `bedrock/anthropic/haiku/4.5` says Haiku sits below Opus. Keyed
 * by company; families not listed rank after listed ones (alphabetically).
 */
const FAMILY_RANK: ReadonlyMap<string, readonly string[]> = new Map([
  ['anthropic', ['fable', 'opus', 'sonnet', 'haiku']],
]);

/**
 * Split a trailing version off a model slug: `glm-5.2` → base `glm` + [5,2],
 * `deepseek-v3.2` → `deepseek` + [3,2], `kimi-k2.5` → `kimi` + [2,5]. The
 * version must be the LAST dash-segment, all digits/dots, with at most a
 * single-letter prefix (v3.2, k2.5). Segments ENDING in a letter (`480b`,
 * `27b`) are param sizes, not versions, and deliberately don't match — so
 * `gpt-oss` stays whole rather than losing `oss`.
 */
const SLUG_VERSION_RE = /-[a-z]?(\d+(?:\.\d+)*)$/iu;

function splitSlugVersion(slug: string): {
  readonly base: string;
  readonly tuple: readonly number[];
} {
  const m = SLUG_VERSION_RE.exec(slug);
  if (m == null) return { base: slug, tuple: [] };
  return {
    base: slug.slice(0, m.index),
    tuple: m[1]!.split('.').map(Number),
  };
}

/**
 * Descending element-wise version compare; an absent element sorts lowest,
 * so `5.2 > 5.1 > 5 > 4.10 > 4.9` (integer-wise — parseFloat would put
 * 4.9 above 4.10).
 */
function compareTuplesDesc(a: readonly number[], b: readonly number[]): number {
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const d = (b[i] ?? -1) - (a[i] ?? -1);
    if (d !== 0) return d;
  }
  return 0;
}

/**
 * Sort key for a version segment (segment 4), where versions and variants
 * mix freely: `5`, `4.8`, `max`, `120b`, `coder-480b`, `3.7-max`.
 */
interface VersionSegmentKey {
  readonly tuple: readonly number[];
  /** `max` above bare above `mini`/`lite`/`flash`/`nano`. */
  readonly tier: number;
  /** Param count for `480b`-style tokens; -1 when absent. */
  readonly params: number;
}

function parseVersionSegment(version: string): VersionSegmentKey {
  let tuple: readonly number[] = [];
  let tier = 0;
  let params = -1;
  for (const token of version.toLowerCase().split('-')) {
    if (tuple.length === 0 && /^v?\d+(?:\.\d+)*$/u.test(token)) {
      tuple = token.replace(/^v/u, '').split('.').map(Number);
    } else if (/^\d+b$/u.test(token)) {
      params = Math.max(params, parseInt(token, 10));
    } else if (token === 'max') {
      tier = 1;
    } else if (['mini', 'lite', 'flash', 'nano'].includes(token)) {
      tier = -1;
    }
  }
  return { tuple, tier, params };
}

/**
 * Order version entries within one model's flyout: legacy last, then version
 * desc, tier desc (`5-max` > `5` > `5-mini`), param count desc (`480b` >
 * `235b`), raw segment, and provider as the multi-provider tiebreak.
 */
export function sortVersionEntries(
  versions: readonly ModelVersionEntry[],
): readonly ModelVersionEntry[] {
  return [...versions].sort((a, b) => {
    const legacy = Number(a.legacy === true) - Number(b.legacy === true);
    if (legacy !== 0) return legacy;
    const ka = parseVersionSegment(a.parsed.version);
    const kb = parseVersionSegment(b.parsed.version);
    return (
      compareTuplesDesc(ka.tuple, kb.tuple) ||
      kb.tier - ka.tier ||
      kb.params - ka.params ||
      a.parsed.version.localeCompare(b.parsed.version) ||
      a.parsed.provider.localeCompare(b.parsed.provider)
    );
  });
}

// ── Version grouping within a company (for the picker sub-menus) ───────

export interface ModelVersionEntry extends ModelInfo {
  readonly parsed: ParsedModelId;
}

export interface ModelVersionGroup {
  /** Base model name (e.g. "Opus", "DeepSeek V3"). */
  readonly modelName: string;
  /**
   * Every selectable variant — each provider×version combination is its own
   * entry so the picker can use the real model `id` directly. The list is NOT
   * merged: `bedrock/moonshot-ai/kimi-k3/` and `fireworks-ai/moonshot-ai/kimi-k3/`
   * are two entries, each with its own id.
   */
  readonly versions: readonly ModelVersionEntry[];
  /** True when the same company/model is served by more than one provider. */
  readonly hasMultipleProviders: boolean;
}

/**
 * Within a flat list of models (already filtered to one company), group by
 * base model name. Each provider variant stays as its own entry.
 *
 * Groups are ordered most-capable first: FAMILY_RANK where the company has
 * one (Fable > Opus > Sonnet > Haiku), then base name, then slug version
 * descending (GLM 5.2 > GLM 5 > GLM 4.7; Kimi K3 > Kimi K2.5). Groups whose
 * every entry is legacy sink to the bottom. Versions within each group are
 * pre-sorted via `sortVersionEntries`.
 */
export function groupByModelAndVersion(
  models: readonly ModelInfo[],
): readonly ModelVersionGroup[] {
  const byModel = new Map<
    string,
    {
      modelName: string;
      parsed: ParsedModelId;
      entries: ModelVersionEntry[];
      providers: Set<string>;
    }
  >();

  for (const m of models) {
    const parsed = parseModelId(m.id);
    const modelKey = `${parsed.company}/${parsed.model}`.toLowerCase();
    let group = byModel.get(modelKey);
    if (group == null) {
      group = {
        modelName: formatModelName(parsed.model),
        parsed,
        entries: [],
        providers: new Set(),
      };
      byModel.set(modelKey, group);
    }
    group.entries.push({ ...m, parsed });
    group.providers.add(parsed.provider);
  }

  return [...byModel.values()]
    .map((group) => {
      const { base, tuple } = splitSlugVersion(
        group.parsed.model.toLowerCase(),
      );
      const rank = FAMILY_RANK.get(group.parsed.company.toLowerCase());
      const idx = rank?.indexOf(base) ?? -1;
      return {
        modelName: group.modelName,
        versions: sortVersionEntries(group.entries),
        hasMultipleProviders: group.providers.size > 1,
        base,
        tuple,
        familyRank: idx === -1 ? Number.MAX_SAFE_INTEGER : idx,
        allLegacy: group.entries.every((e) => e.legacy === true),
      };
    })
    .sort(
      (a, b) =>
        Number(a.allLegacy) - Number(b.allLegacy) ||
        a.familyRank - b.familyRank ||
        a.base.localeCompare(b.base) ||
        compareTuplesDesc(a.tuple, b.tuple),
    )
    .map(({ modelName, versions, hasMultipleProviders }) => ({
      modelName,
      versions,
      hasMultipleProviders,
    }));
}

/**
 * Casing overrides for slug tokens that plain title-case gets wrong —
 * acronyms (`glm` → `GLM`, not `Glm`) and brand spellings (`deepseek` →
 * `DeepSeek`; keep in sync with COMPANY_LABELS).
 */
const WORD_CASE: ReadonlyMap<string, string> = new Map([
  ['glm', 'GLM'],
  ['gpt', 'GPT'],
  ['oss', 'OSS'],
  ['ai', 'AI'],
  ['deepseek', 'DeepSeek'],
]);

/**
 * Pretty-print a model slug: `opus` → `Opus`, `deepseek-v3` → `DeepSeek V3`,
 * `qwen-3.7` → `Qwen 3.7`, `glm-5` → `GLM 5`, `gpt-oss` → `GPT OSS`.
 */
function formatModelName(slug: string): string {
  return slug
    .split('-')
    .map((w) => {
      const special = WORD_CASE.get(w.toLowerCase());
      if (special != null) return special;
      if (/^\d/u.test(w)) return w; // version-like segments stay lowercase
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(' ')
    .replace(/\s+v(\d)/giu, ' V$1'); // "V3" not "v3"
}

/**
 * Format a version entry label: model + version.
 * `opus` + `4.8` → `Opus 4.8`, `deepseek-v3` + `` → `DeepSeek V3`.
 */
export function formatVersionLabel(parsed: ParsedModelId): string {
  const name = formatModelName(parsed.model);
  return parsed.version.length > 0 ? `${name} ${parsed.version}` : name;
}
